import { NavLink, useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/lib/AuthContext";
import { LogOut, Users, HelpCircle } from "lucide-react";

const tabs = [
  { to: "/teacher", label: "My guests", end: true, icon: Users },
];

export default function TeacherLayout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    navigate("/teacher/login");
  };

  const firstName = user?.name?.split(" ")?.[0] || user?.email?.split("@")?.[0];

  return (
    <div className="min-h-screen flex flex-col" style={{ background: "var(--bg-base)" }} data-testid="teacher-layout">
      <header
        className="border-b sticky top-0 z-30 backdrop-blur"
        style={{
          borderColor: "var(--border-subtle)",
          background: "color-mix(in srgb, var(--bg-alt) 88%, transparent)",
        }}
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-4 flex items-center justify-between gap-4 flex-wrap">
          <Link to="/teacher" data-testid="teacher-brand" className="flex flex-col leading-tight">
            <span className="font-serif-display text-xl lg:text-2xl tracking-[-0.03em]" style={{ color: "var(--ink)" }}>
              Home Study <span style={{ color: "var(--gold)" }}>Portal</span>
            </span>
            <span className="text-[0.55rem] uppercase tracking-[0.32em] mt-0.5 font-semibold" style={{ color: "var(--muted)" }}>
              Bible Study Teacher
            </span>
          </Link>

          {user && (
            <div className="flex items-center gap-4 flex-wrap">
              <nav className="flex items-center gap-1 flex-wrap" data-testid="teacher-tabs">
                {tabs.map((t) => {
                  const Icon = t.icon;
                  return (
                    <NavLink
                      key={t.to}
                      to={t.to}
                      end={t.end}
                      data-testid={`teacher-tab-${t.label.toLowerCase().replace(/ /g, "-")}`}
                      className={({ isActive }) =>
                        `flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-full transition-colors ${
                          isActive
                            ? "bg-[var(--ink)] text-[var(--bg-base)]"
                            : "text-[var(--ink-2)] hover:bg-[var(--surface-hover)]"
                        }`
                      }
                    >
                      <Icon size={14} />
                      {t.label}
                    </NavLink>
                  );
                })}
                <a
                  href="mailto:thedisciplelifems@gmail.com?subject=Home%20Study%20Portal%20Help"
                  className="flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-full text-[var(--ink-2)] hover:bg-[var(--surface-hover)]"
                  data-testid="teacher-help-link"
                >
                  <HelpCircle size={14} /> Help
                </a>
              </nav>
              <div className="flex items-center gap-3 pl-4 border-l" style={{ borderColor: "var(--border-subtle)" }}>
                {firstName && (
                  <span className="hidden sm:inline text-xs" style={{ color: "var(--muted)" }} data-testid="teacher-user-name">
                    Hi, <span style={{ color: "var(--ink)" }}>{firstName}</span>
                  </span>
                )}
                <button
                  onClick={handleLogout}
                  data-testid="teacher-logout-btn"
                  className="flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-full transition-colors hover:bg-[var(--surface-hover)]"
                  style={{ color: "var(--terracotta)" }}
                >
                  <LogOut size={14} /> Sign out
                </button>
              </div>
            </div>
          )}
        </div>
      </header>

      <main className="flex-1">{children}</main>

      <footer className="border-t py-4 text-xs text-center" style={{ borderColor: "var(--border-subtle)", color: "var(--muted)" }}>
        © {new Date().getFullYear()} The Disciple Life · Home Study Portal
      </footer>
    </div>
  );
}
